import { PASSWORD_MIN_LENGTH, type UserSessionDto } from '@cartly/shared';
import { Alert, Badge, Button, Card, Modal, Spinner, TextField, useToast } from '@cartly/ui';
import { Monitor, ShieldCheck } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { describeUserAgent, formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

function ChangePasswordCard() {
  const toast = useToast();
  const submit = useSubmit();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '' });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.post('/auth/password/change', form);
      return true;
    });
    if (!ok) return;
    setForm({ currentPassword: '', newPassword: '' });
    toast.success('Password changed. Other sessions were signed out.');
  };

  return (
    <Card title="Password" subtitle="Changing your password signs out your other sessions.">
      <form className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <div className="form-grid">
          <TextField
            label="Current password"
            type="password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
            error={submit.fieldErrors['currentPassword']}
          />
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            value={form.newPassword}
            onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
            hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}
            error={submit.fieldErrors['newPassword']}
          />
        </div>
        <div className="form-actions">
          <Button type="submit" variant="primary" loading={submit.pending}>
            Change password
          </Button>
        </div>
      </form>
    </Card>
  );
}

function MfaSetupModal({ onClose, onEnabled }: { onClose: () => void; onEnabled: () => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string; qr: string } | null>(
    null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const submit = useSubmit();

  useEffect(() => {
    let cancelled = false;
    api
      .post<{ secret: string; otpauthUri: string }>('/auth/mfa/totp/setup')
      .then(async (s) => {
        const qr = await QRCode.toDataURL(s.otpauthUri, { margin: 1, width: 192 });
        if (!cancelled) setSetup({ ...s, qr });
      })
      .catch((err: unknown) => !cancelled && setLoadError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    const result = await submit.run(() =>
      api.post<{ recoveryCodes: string[] }>('/auth/mfa/totp/confirm', { code }),
    );
    if (result) setRecoveryCodes(result.recoveryCodes);
  };

  const finish = () => {
    onEnabled();
    onClose();
  };

  return (
    <Modal
      open
      title="Set up two-factor authentication"
      onClose={recoveryCodes ? finish : onClose}
      footer={
        recoveryCodes ? (
          <Button variant="primary" onClick={finish}>
            I've saved my recovery codes
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="mfa-form"
              loading={submit.pending}
              disabled={!setup}
            >
              Turn on
            </Button>
          </>
        )
      }
    >
      {loadError ? (
        <Alert tone="critical">{loadError}</Alert>
      ) : recoveryCodes ? (
        <div className="stack">
          <Alert tone="warning" title="Save these recovery codes">
            Each code works once if you lose access to your authenticator. They won't be shown
            again.
          </Alert>
          <ul className="recovery-codes">
            {recoveryCodes.map((c) => (
              <li key={c} className="mono">
                {c}
              </li>
            ))}
          </ul>
        </div>
      ) : !setup ? (
        <Spinner size="lg" label="Preparing" />
      ) : (
        <form id="mfa-form" className="stack" onSubmit={confirm} noValidate>
          <p>
            Scan this QR code with an authenticator app such as Google Authenticator, 1Password or
            Authy.
          </p>
          <img
            className="mfa-qr"
            src={setup.qr}
            alt="QR code for your authenticator app"
            width={192}
            height={192}
          />
          <p className="text-sm text-muted">
            Can't scan? Enter this key manually: <code className="mfa-secret">{setup.secret}</code>
          </p>
          {submit.error && <Alert tone="critical">{submit.error}</Alert>}
          <TextField
            label="6-digit code from the app"
            className="input--code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            error={submit.fieldErrors['code']}
          />
        </form>
      )}
    </Modal>
  );
}

function DisableMfaModal({ onClose, onDisabled }: { onClose: () => void; onDisabled: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const submit = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit.run(async () => {
      await api.post('/auth/mfa/disable', { password, code });
      return true;
    });
    if (!ok) return;
    onDisabled();
    onClose();
  };

  return (
    <Modal
      open
      title="Turn off two-factor authentication"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" type="submit" form="mfa-disable-form" loading={submit.pending}>
            Turn off
          </Button>
        </>
      }
    >
      <form id="mfa-disable-form" className="stack" onSubmit={onSubmit} noValidate>
        {submit.error && <Alert tone="critical">{submit.error}</Alert>}
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={submit.fieldErrors['password']}
        />
        <TextField
          label="Authentication code"
          className="input--code"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          error={submit.fieldErrors['code']}
        />
      </form>
    </Modal>
  );
}

function MfaCard() {
  const session = useActiveSession();
  const { refresh } = useSession();
  const toast = useToast();
  const [modal, setModal] = useState<'setup' | 'disable' | null>(null);
  const enabled = session.user.mfaEnabled;

  return (
    <Card
      title="Two-factor authentication"
      subtitle="Require a code from your authenticator app when signing in."
      actions={enabled ? <Badge tone="success">On</Badge> : <Badge tone="warning">Off</Badge>}
    >
      <div className="row-between">
        <div className="row text-muted">
          <ShieldCheck size={18} />
          {enabled
            ? 'Your account is protected with an authenticator app.'
            : 'Add a second step to protect your store data and customer conversations.'}
        </div>
        {enabled ? (
          <Button variant="secondary" onClick={() => setModal('disable')}>
            Turn off
          </Button>
        ) : (
          <Button variant="primary" onClick={() => setModal('setup')}>
            Set up
          </Button>
        )}
      </div>
      {modal === 'setup' && (
        <MfaSetupModal
          onClose={() => setModal(null)}
          onEnabled={() => {
            toast.success('Two-factor authentication is on');
            void refresh();
          }}
        />
      )}
      {modal === 'disable' && (
        <DisableMfaModal
          onClose={() => setModal(null)}
          onDisabled={() => {
            toast.info('Two-factor authentication is off');
            void refresh();
          }}
        />
      )}
    </Card>
  );
}

function SessionsCard() {
  const toast = useToast();
  const { data, error, loading, reload } = useLoader(() =>
    api.get<{ items: UserSessionDto[] }>('/auth/sessions').then((r) => r.items),
  );

  const revoke = async (id: string) => {
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success('Session signed out');
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const revokeOthers = async () => {
    try {
      const { revoked } = await api.post<{ revoked: number }>('/auth/sessions/revoke-others');
      toast.success(`Signed out of ${revoked} other ${revoked === 1 ? 'session' : 'sessions'}`);
      await reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Card
      title="Active sessions"
      subtitle="Devices where you're signed in."
      actions={
        data && data.length > 1 ? (
          <Button size="sm" onClick={() => void revokeOthers()}>
            Sign out other sessions
          </Button>
        ) : undefined
      }
      flush
    >
      {loading && !data ? (
        <div className="card__body">
          <Spinner />
        </div>
      ) : error || !data ? (
        <div className="card__body">
          <Alert tone="critical">{error}</Alert>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <tbody>
              {data.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="row">
                      <Monitor size={18} className="text-muted" />
                      <div>
                        <div className="table__primary">
                          {describeUserAgent(s.userAgent)}{' '}
                          {s.current && <Badge tone="success">This device</Badge>}
                        </div>
                        <div className="table__secondary">
                          {s.ipAddress ?? 'Unknown IP'} · active {formatRelative(s.lastSeenAt)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="table__actions">
                    {!s.current && (
                      <Button size="sm" variant="ghost" onClick={() => void revoke(s.id)}>
                        Sign out
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export function SecuritySettings() {
  return (
    <div className="stack-lg">
      <MfaCard />
      <ChangePasswordCard />
      <SessionsCard />
    </div>
  );
}
